import { c as _c } from "react/compiler-runtime";
import { placementEngine } from "@theconversation/promos-client";
import { THE_CONVERSATION_VENDOR_ID, THE_CONVERSATION_PROMOS_PURPOSE } from "@theconversation/shared-libraries/Didomi";
import { createContext, PropsWithChildren, useEffect, useState } from "react";
import { usePromosData } from "modules/promos/hooks/usePromosData";
import throttle from "lodash/throttle";
import type { PromoDataProps } from "modules/promos/types";
import { EffectFn1 } from "common/lib/types";
import { useConsent } from "common/hooks/data/useConsent";
import { getLocalStorage } from "common/lib/getLocalStorage";
import { useRegionCode } from "modules/i18n/hooks/useRegionCode";
import { useViewer } from "common/hooks/data/useViewer";
import { useViewerSubscriptions } from "common/hooks/data/useViewerSubscriptions";

// A `PlacementEngineEvent` defines events coming from the placement engine. These events require the promo as
// an argument, so the placement engine can update the user local storage based on user interaction (engagement, block, etc.)
export type PlacementEngineEvent = EffectFn1<PromoDataProps>;

// The placement engine observable is the resulting object after it processes the promos pipeline. It is accessible
// by subscribing the engine's signal.
type PlacementEngineObservable = {
  promos: PromoDataProps[];
  onClose: PlacementEngineEvent;
  onClick: PlacementEngineEvent;
  onView: PlacementEngineEvent;
  onRefresh: PlacementEngineEvent;
};
export type PromosPlacementEngineProps = {
  promos: PromoDataProps[];
  events: {
    onClose: PlacementEngineEvent;
    onClick: PlacementEngineEvent;
    onView: PlacementEngineEvent;
  };
};
export const PromosPlacementEngineContext = createContext<PromosPlacementEngineProps>({
  promos: [],
  events: {
    onClose: () => {},
    onClick: () => {},
    onView: () => {}
  }
});
export const PromosContext = t0 => {
  "use memo";

  const $ = _c(30);
  const {
    children
  } = t0;
  const candidatePromos = usePromosData();
  const consent = useConsent(THE_CONVERSATION_VENDOR_ID, THE_CONVERSATION_PROMOS_PURPOSE);
  const region = useRegionCode();
  const {
    viewer,
    loaded
  } = useViewer();
  const {
    getAllSubscribedNewsletters
  } = useViewerSubscriptions();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [promos, setPromos] = useState(t1);
  const [onClose, setOnClose] = useState(_temp2);
  const [onClick, setOnClick] = useState(_temp4);
  const [onView, setOnView] = useState(_temp6);
  const [onRefresh, setOnRefresh] = useState(_temp8);
  let t2;
  if ($[1] !== candidatePromos || $[2] !== consent || $[3] !== getAllSubscribedNewsletters || $[4] !== loaded || $[5] !== region || $[6] !== viewer?.user?.donations) {
    t2 = () => {
      if (!loaded) {
        return;
      }
      if (candidatePromos.length === 0) {
        return;
      }
      const context = {
        consent,
        region,
        user: {
          donations: viewer?.user?.donations,
          newsletters: {
            subscribedIds: getAllSubscribedNewsletters().map(_temp9)
          }
        }
      };
      const signal = placementEngine(getLocalStorage(), candidatePromos, context);
      signal.subscribe(observable => {
        setPromos(observable.promos);
        setOnClose(() => observable.onClose);
        setOnClick(() => observable.onClick);
        setOnView(() => observable.onView);
        setOnRefresh(() => observable.onRefresh);
      });
      return () => {
        signal.unsubscribe();
      };
    };
    $[1] = candidatePromos;
    $[2] = consent;
    $[3] = getAllSubscribedNewsletters;
    $[4] = loaded;
    $[5] = region;
    $[6] = viewer?.user?.donations;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== candidatePromos || $[9] !== consent || $[10] !== getAllSubscribedNewsletters || $[11] !== loaded || $[12] !== region || $[13] !== viewer) {
    t3 = [candidatePromos, consent, region, loaded, viewer, getAllSubscribedNewsletters];
    $[8] = candidatePromos;
    $[9] = consent;
    $[10] = getAllSubscribedNewsletters;
    $[11] = loaded;
    $[12] = region;
    $[13] = viewer;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  useEffect(t2, t3);
  let t4;
  if ($[15] !== onRefresh) {
    t4 = () => {
      const handleRefresh = throttle(() => onRefresh(), 1000);
      document.addEventListener("resize", handleRefresh);
      document.addEventListener("scroll", handleRefresh, {
        passive: true
      });
      return () => {
        document.removeEventListener("scroll", handleRefresh);
        document.removeEventListener("resize", handleRefresh);
      };
    };
    $[15] = onRefresh;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== candidatePromos || $[18] !== onRefresh) {
    t5 = [candidatePromos, onRefresh];
    $[17] = candidatePromos;
    $[18] = onRefresh;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  useEffect(t4, t5);
  let t6;
  if ($[20] !== onClick || $[21] !== onClose || $[22] !== onView) {
    t6 = {
      onClose,
      onClick,
      onView
    };
    $[20] = onClick;
    $[21] = onClose;
    $[22] = onView;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== promos || $[25] !== t6) {
    t7 = {
      promos,
      events: t6
    };
    $[24] = promos;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== children || $[28] !== t7) {
    t8 = <PromosPlacementEngineContext.Provider value={t7}>{children}</PromosPlacementEngineContext.Provider>;
    $[27] = children;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
};
function _temp() {}
function _temp2() {
  return _temp;
}
function _temp3() {}
function _temp4() {
  return _temp3;
}
function _temp5() {}
function _temp6() {
  return _temp5;
}
function _temp7() {}
function _temp8() {
  return _temp7;
}
function _temp9(t0) {
  const {
    id
  } = t0;
  return id;
}